
import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { TaskWithLead } from '@/types/taskFilters';
import { TaskCard } from './TaskCard';

interface TaskColumnProps {
  id: string;
  title: string;
  tasks: TaskWithLead[];
  count: number;
  onEdit: (task: TaskWithLead) => void;
  onDelete: (taskId: string) => void;
  onComplete: (taskId: string) => void;
  getPriorityBadge: (priority: string) => React.ReactNode;
}

export const TaskColumn: React.FC<TaskColumnProps> = ({
  id,
  title,
  tasks,
  count,
  onEdit,
  onDelete,
  onComplete,
  getPriorityBadge
}) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  
  return (
    <div 
      className="flex flex-col bg-gray-50/50 rounded-xl border border-gray-200 min-h-[400px] md:min-h-[500px] w-[calc(100vw-3rem)] min-w-[280px] sm:w-[320px] md:w-80 snap-center md:snap-align-none flex-shrink-0"
    >
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 bg-white rounded-t-xl">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-base">{title}</h3>
          <div className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full font-medium">{count}</div>
        </div>
      </div>
      
      <div
        ref={setNodeRef}
        className={`flex-1 p-2 sm:p-3 space-y-2 sm:space-y-2.5 overflow-y-auto ${isOver ? 'bg-gray-100/50' : ''}`}
        style={{ minHeight: '200px' }}
      >
        <SortableContext items={tasks.map(task => task.id)} strategy={verticalListSortingStrategy}>
          {tasks.length > 0 ? (
            tasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                onEdit={onEdit}
                onDelete={onDelete}
                onComplete={onComplete}
                getPriorityBadge={getPriorityBadge}
              />
            ))
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-4 text-center">
              Drop tasks here to move them to this status
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
};
